import React, { useEffect, useMemo, useState } from 'react';
import { useSignedIn } from '../session';
import { ItemReservationResult, NetworkLocation, SourcingDelivery, SourcingItem } from '../types';
import Dialog from '../components/Dialog';
import { bezIzabranogMesta, nemaNovihIzmena, podrazumevanoMesto, spremnoZaPotvrdu } from './potvrda';

import { API_BASE as BASE } from '../services/apiBase';
import { proveriOdgovor } from '../services/greska';
import { api } from '../services/api';

interface Props {
  code: string;
  deliveries: SourcingDelivery[];
  onSaved: () => void;
}

/** „MPO 009" → { kind: 0, code: "009" }; „MAG 021" → { kind: 1, code: "021" }. */
function parseSource(label: string): { sourceKind: number; sourceCode: string } {
  const [prefix, code] = label.split(' ');
  return { sourceKind: prefix === 'MAG' ? 1 : 0, sourceCode: code ?? label };
}

/**
 * Odluka poslovođe odakle se roba diže.
 *
 * Aplikacija ume da pročita gde robe ima, ali ne i odakle će stvarno ići —
 * zaliha je po artiklu, a ne po komadu, i ne zna šta je obećano kome. Poslovođa
 * to zna, jer je prodaja njegova.
 *
 * Odluka je po stavci, pa jedna isporuka sme da se deli: veš mašina iz radnje,
 * frižider iz magacina. Kad postoji mesto koje drži sve, nudi se prvo — jedan
 * dolazak je obično vredniji od pokojeg kilometra.
 */
export default function SourcingPanel({ code, deliveries, onSaved }: Props) {
  /**
   * Otpremnica za koju se upravo pušta „roba je stigla".
   *
   * Rezervacije tu nema i ne sme je biti: prenos u 012 je prenos robe u ERP-u, a
   * ERP nema šta da prenese dok kalkulacija ne uđe. Roba u tom prozoru nije
   * zaštićena od druge prodaje — i to operater vidi na svom ekranu.
   */
  const [stigla, setStigla] = useState<string | null>(null);

  /**
   * Poruka o padu UZ RED na koji je kliknuto.
   *
   * Do 29.09.2026. je greska isla samo u traku na VRHU panela. Kad spisak ima
   * dvadeset isporuka, poslovodja klikne na dnu i ne vidi nista — pa je prijavio
   * da se „apsolutno nista ne desava", iako je server uredno odgovarao 403.
   */
  const [padPoDokumentu, setPadPoDokumentu] = useState<Record<string, string>>({});

  const javiDaJeStigla = async (documentKey: string) => {
    setStigla(documentKey);
    setPadPoDokumentu((p) => { const { [documentKey]: _, ...rest } = p; return rest; });

    try {
      await api.robaJeStiglaZaObjekat(code, documentKey);
      onSaved();
    } catch (e) {
      const poruka = e instanceof Error ? e.message : String(e);
      setError(poruka);
      setPadPoDokumentu((p) => ({ ...p, [documentKey]: poruka }));
    } finally {
      setStigla(null);
    }
  };

  /**
   * „Kupac preuzeo ovde" — sopstvena prodaja koju je kupac uzeo u ovom objektu.
   *
   * Mejl operatera 14.10.2026: dve otpremnice SO1 koje su kupci preuzeli u
   * radnji stajale su mu za razvoz, jer je ovde postojalo samo pitanje
   * „odakle se roba diže".
   */
  const [preuzima, setPreuzima] = useState<SourcingDelivery | null>(null);

  /**
   * Potvrda mesta odmah pravi nalog u ERP-u, pa se pita pre upisa — kao i
   * „Kupac preuzeo ovde". Do 16.10.2026. je to bio jedan klik bez pitanja, a
   * pogrešno mesto nije moglo da se promeni (analiza scenarija, K7).
   */
  const [potvrdaZa, setPotvrdaZa] = useState<SourcingDelivery | null>(null);
  const [preuzetoIshod, setPreuzetoIshod] = useState<Record<string, string>>({});

  const preuzeoOvde = async () => {
    if (!preuzima) return;
    const kljuc = preuzima.documentKey;
    setBusy(kljuc);
    try {
      const odgovor = await fetch(`${BASE}/api/store/${code}/preuzeo-ovde`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentKey: kljuc })
      });
      await proveriOdgovor(odgovor, `/api/store/${code}/preuzeo-ovde`);
      const ishod = await odgovor.json();
      setPreuzetoIshod((p) => ({ ...p, [kljuc]: ishod.poruka }));
      setPreuzima(null);
      onSaved();
    } catch (e) {
      // Odbijeno (na turi, odvojeno iz drugog mesta) stoji UZ RED — mora da se pročita.
      setPreuzetoIshod((p) => ({ ...p, [kljuc]: e instanceof Error ? e.message : String(e) }));
      setPreuzima(null);
    } finally {
      setBusy(null);
    }
  };

  const signedIn = useSignedIn();
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Šta je odluka uradila u ERP-u, po otpremnici.
   *
   * Stoji na ekranu jer rezervacija menja tuđe knjige: poslovođa mora da vidi
   * broj naloga, i još više — da vidi kad prenos NIJE prošao, pa da robu odvoji
   * ručno umesto da veruje da je odvojena.
   */
  const [reservations, setReservations] = useState<Record<string, ItemReservationResult[]>>({});

  /**
   * Potvrda da je klik uhvacen, po otpremnici.
   *
   * Do sada se posle cuvanja nije menjalo NISTA vidljivo: kartica ista, dugme
   * se ugasi pa upali. Odluka jeste ulazila u bazu, ali covek to nije mogao da
   * zna — pa je klikao ponovo.
   */
  const [sacuvano, setSacuvano] = useState<Record<string, { stavki: number; kad: string }>>({});

  const keyOf = (documentKey: string, articleCode: string) => `${documentKey}|${articleCode}`;

  /**
   * Stavka za koju poslovođa javlja da je roba naručena.
   *
   * Do sada je operater dobijao golo „nema na stanju nigde" i sa tim nije mogao
   * ništa — tražio je zamenu ili zvao kupca, iako je roba već bila naručena.
   */
  const [promising, setPromising] = useState<{ document: string; item: SourcingItem } | null>(null);
  const [promiseWhere, setPromiseWhere] = useState('');
  const [promiseWhen, setPromiseWhen] = useState('');
  const [promiseNote, setPromiseNote] = useState('');
  const [places, setPlaces] = useState<NetworkLocation[]>([]);

  useEffect(() => {
    void fetch(`${BASE}/api/locations`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then(setPlaces)
      .catch(() => setPlaces([]));
  }, []);

  /**
   * Skidanje obecanja — pogresno uneto, ili se roba ipak nece pojaviti.
   *
   * Cim ono padne, druga mesta se opet nude i stavka trazi izbor.
   */
  const zaboravi = async (documentKey: string, articleCode: string) => {
    setBusy(documentKey);
    try {
      const putanja = `${BASE}/api/store/${code}/expected`
        + `?documentKey=${encodeURIComponent(documentKey)}`
        + `&articleCode=${encodeURIComponent(articleCode)}`;

      const response = await fetch(putanja, { method: 'DELETE', credentials: 'include' });

      await proveriOdgovor(response, `/api/store/${code}/expected`);

      onSaved();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const promise = async () => {
    if (!promising || promiseWhere === '' || promiseWhen === '') return;

    const [kind, place] = promiseWhere.split(':');

    setBusy(promising.document);
    try {
      const response = await fetch(`${BASE}/api/store/${code}/expected`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentKey: promising.document,
          articleCode: promising.item.articleCode,
          expectedKind: kind,
          expectedCode: place,
          expectedOn: promiseWhen,
          note: promiseNote.trim() || null
        })
      });

      await proveriOdgovor(response, `/api/store/${code}/expected`);

      setPromising(null);
      setPromiseWhere('');
      setPromiseWhen('');
      setPromiseNote('');
      onSaved();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Šta je izabrano za stavku: ono što poslovođa upravo bira, pa ranija
   * odluka, pa TRGOVČEV UPIS sa računa.
   *
   * Trgovac je izvor upisao u trenutku prodaje, dok je stanje bilo tačno;
   * poslovođa sutradan bira nad zalihom koja se u međuvremenu promenila.
   * Zato njegov upis dolazi kao podrazumevan — poslovođi ostaje da potvrdi.
   */
  const chosenFor = (delivery: SourcingDelivery, item: SourcingItem): string =>
    choices[keyOf(delivery.documentKey, item.articleCode)]
    ?? podrazumevanoMesto(item);

  const choose = (documentKey: string, articleCode: string, source: string) =>
    setChoices((current) => ({ ...current, [keyOf(documentKey, articleCode)]: source }));

  /** Jedan klik postavlja sve stavke na isto mesto. Najčešći slučaj. */
  const chooseAll = (delivery: SourcingDelivery, source: string) =>
    setChoices((current) => {
      const next = { ...current };
      for (const item of delivery.items) {
        next[keyOf(delivery.documentKey, item.articleCode)] = source;
      }
      return next;
    });

  const save = async (delivery: SourcingDelivery) => {
    const items = delivery.items
      .map((item) => ({ item, source: chosenFor(delivery, item) }))
      .filter(({ source }) => source !== '')
      .map(({ item, source }) => ({ articleCode: item.articleCode, ...parseSource(source) }));

    if (items.length === 0) {
      setError('Nijedna stavka nema izabrano mesto.');
      return;
    }

    setBusy(delivery.documentKey);
    try {
      const response = await fetch(`${BASE}/api/store/${code}/sourcing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ documentKey: delivery.documentKey, items })
      });

      await proveriOdgovor(response, `/api/store/${code}/sourcing`);

      const result = await response.json();
      setReservations((current) => ({
        ...current,
        [delivery.documentKey]: Array.isArray(result?.reservations) ? result.reservations : []
      }));

      setSacuvano((current) => ({
        ...current,
        [delivery.documentKey]: {
          stavki: typeof result?.decided === 'number' ? result.decided : items.length,
          kad: new Date().toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' })
        }
      }));

      setError(null);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Isporuke na istoj adresi idu zajedno.
   *
   * Kupac koji je kupio šporet i frižider ima dve otpremnice, a to je jedan
   * dolazak na jedna vrata. Dok su stajale kao dve kartice, poslovođa je istu
   * adresu čitao dvaput i lako je promašio da su to isti ljudi.
   *
   * Odluka ostaje po otpremnici — ERP je vodi tako — pa svaka ima svoje polje i
   * svoje dugme; zajedničko je samo zaglavlje.
   */
  const byAddress = useMemo(() => {
    const map = new Map<string, { label: string; customer: string; waitingDays: number; items: SourcingDelivery[] }>();

    for (const delivery of deliveries) {
      // Roba za kupca koji dolazi u objekat nema adresu na koju se vozi — piše
      // kuda ide.
      const label = delivery.uObjekat
        ? `kupac dolazi po robu u ${delivery.uObjekat}`
        : `${delivery.address}, ${delivery.city}`;
      const key = `${label}|${delivery.customer}`;
      const bucket = map.get(key);

      if (bucket) {
        bucket.items.push(delivery);
        bucket.waitingDays = Math.max(bucket.waitingDays, delivery.waitingDays);
      } else {
        map.set(key, {
          label,
          customer: delivery.customer || 'bez imena',
          waitingDays: delivery.waitingDays,
          items: [delivery]
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => b.waitingDays - a.waitingDays);
  }, [deliveries]);

  if (deliveries.length === 0) {
    return <p className="empty">Nema isporuka koje čekaju odluku odakle roba ide.</p>;
  }

  return (
    <>
      <p className="note">
        Aplikacija vidi gde robe ima, ali ne i odakle će stvarno ići. To znaš ti —
        prodaja je tvoja. Odluka se odmah vidi u magacinu, i potpisuje se tvojim
        nalogom ({signedIn.displayName}).
      </p>

      {error && <div className="health health--bad">{error}</div>}

      <ul className="sourcing">
        {byAddress.map((group) => (
          <li key={group.label + group.customer}>
            <p className="sourcing__head">
              {group.label}
              <span className="muted">
                {' '}· {group.customer} · čeka {group.waitingDays} d
                {group.items.length > 1 && ` · ${group.items.length} otpremnice`}
              </span>
            </p>

            {group.items.map((delivery) => {
              const decided = spremnoZaPotvrdu(
                delivery.items, (item) => chosenFor(delivery, item));

              // Sta tacno drzi dugme. Bez ovoga je sivo dugme zagonetka.
              const fale = bezIzabranogMesta(
                delivery.items, (item) => chosenFor(delivery, item));

              const stale = delivery.items.some((i) => !i.stillAvailable);

              // Prenos u 012 nije prošao: potvrda je zabeležena, ali komad nije
              // odvojen — red ne sme da piše „potvrđeno" (K15).
              const palo = delivery.items.filter((i) => i.reservationFailed);

              // Sve je vec potvrdjeno i nista nije promenjeno od tada. Izuzetak
              // je zastarela odluka — nju treba potvrditi ponovo.
              const potvrdjeno = !stale && palo.length === 0 && nemaNovihIzmena(
                delivery.items,
                (item) => chosenFor(delivery, item),
                (item) => item.decidedSource);

              const upravoSacuvano = sacuvano[delivery.documentKey];

              return (
                <div key={delivery.documentKey} className="sourcing__doc">
                  {group.items.length > 1 && (
                    <p className="muted sourcing__dockey">Otpremnica {delivery.documentKey}</p>
                  )}

                  {/* STA SE SA OVIM DESAVA. Poslovodja potvrdi odakle roba ide i
                      tu mu se trag gubio: da li je magacin to video, je li na
                      turi, je li otislo. Odgovor postoji — samo nije stizao do
                      njegovog ekrana. */}
                  <p className={`sourcing__status${
                    delivery.status.startsWith('predato') ? ' sourcing__status--ok' : ''}`}>
                    {delivery.status}
                  </p>

              {stale && (
                <p className="sourcing__stale">
                  Ranija odluka pokazuje na mesto gde robe više nema na stanju.
                  Proveri i potvrdi ponovo.
                </p>
              )}

              {delivery.singleStop.length > 0 && (
                <p className="sourcing__all">
                  Sve odjednom:{' '}
                  {delivery.singleStop.map((option) => (
                    <button
                      key={option.code}
                      type="button"
                      className="link"
                      onClick={() => chooseAll(delivery, option.code)}
                    >
                      {option.name}
                    </button>
                  ))}
                </p>
              )}

              <ul className="sourcing__items">
                {delivery.items.map((item) => (
                  <li key={item.articleCode}>
                    <span className="sourcing__what">
                      {item.articleName} <span className="muted">× {item.quantity}</span>
                    </span>

                    <span className="sourcing__pick">
                    {/* Obecanje potiskuje listu: dok roba stize od dobavljaca,
                        nema sta da se bira. */}
                    {/* Upisana prva noga je jača od obećanja: komad postoji i
                        čeka kamion, pa se mesto bira kao i inače. */}
                    {/* Obećanje bez liste SAMO dok drži stavku. Kad roba stigne
                        (ili odluka na mesto sa robom važi), lista se vraća —
                        analiza 2.0 (I7): stiglo obećanje je imalo mrtvo dugme. */}
                    {item.expected && !item.committed && item.sources.length === 0 ? (
                      <span className={item.expected.overdue ? 'sourcing__none' : 'muted'}>
                        naručeno — stiže u {item.expected.place},{' '}
                        {new Date(item.expected.on).toLocaleDateString('sr-RS')}
                        {item.expected.overdue && <strong> (kasni)</strong>}
                      </span>
                    ) : item.sources.length === 0 ? (
                      <span className="sourcing__none">
                        nema na stanju nigde
                        <button
                          type="button"
                          className="link"
                          onClick={() => {
                            setPromising({ document: delivery.documentKey, item });
                            setPromiseWhere('');
                            setPromiseWhen('');
                            setPromiseNote('');
                          }}
                        >
                          naručeno je, stiže
                        </button>
                      </span>
                    ) : (
                      <select
                        value={chosenFor(delivery, item)}
                        onChange={(e) => choose(delivery.documentKey, item.articleCode, e.target.value)}
                      >
                        <option value="">— odakle se diže —</option>
                        {/* Trgovčev upis stoji i kad zaliha tamo više ne
                            pokazuje robu: poslovođa je možda odvojio komad,
                            a to zaliha po artiklu ne vidi. */}
                        {item.proposedSource
                          && !item.sources.some((o) => o.code === item.proposedSource) && (
                          <option value={item.proposedSource}>
                            {item.proposedName ?? item.proposedSource} — upisao trgovac
                          </option>
                        )}
                        {item.sources.map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.name}
                            {/* Prodati komad nema brojku — zaliha ga ne vidi,
                                jer ga je prodaja skinula sa stanja. */}
                            {option.onTheWay
                              ? ' — roba je odavde već krenula, čeka prevoz'
                              : option.alreadySold
                                ? ' — prodati komad je tu'
                                : option.available != null && ` — ${option.available} kom`}
                          </option>
                        ))}
                      </select>
                    )}
                    </span>

                    <span className="sourcing__notes">
                    {/* Obećanje stoji, a mesto se ipak nudi: roba je stigla, ili
                        važi odluka na mesto gde robe ima. Da se zna zašto. */}
                    {item.expected && !item.committed && item.sources.length > 0 && (
                      <span className="muted">
                        {item.expected.arrived
                          ? `naručena roba je stigla u ${item.expected.place} — potvrdi`
                          : `obećanje za ${item.expected.place} stoji, ali važi odluka`}
                      </span>
                    )}
                    {/* Roba koje IMA u nekoj drugoj radnji, a poslovodja je
                        naruceno od dobavljaca. Bez ovoga mu je jedini ponudjen
                        odgovor bio „dovuci izlozbeni komad iz Subotice". */}
                    {!item.expected && item.sources.length > 0 && (
                      <button
                        type="button"
                        className="link"
                        onClick={() => {
                          setPromising({ document: delivery.documentKey, item });
                          setPromiseWhere('');
                          setPromiseWhen('');
                          setPromiseNote('');
                        }}
                      >
                        ne diži — naručeno od dobavljača
                      </button>
                    )}

                    {item.expected && (
                      <>
                        {item.committed && (
                          <span className="sourcing__last">
                            obećanje ne važi — roba je već krenula iz{' '}
                            {item.committedName ?? item.committed}, poništi ga
                          </span>
                        )}
                        {item.expected.note && (
                          <span className="muted">„{item.expected.note}"</span>
                        )}
                        <button
                          type="button"
                          className="link link--danger"
                          disabled={busy === delivery.documentKey}
                          onClick={() => void zaboravi(delivery.documentKey, item.articleCode)}
                        >
                          poništi
                        </button>
                      </>
                    )}

                    {item.orderedFromSupplier && (
                      <span className="sourcing__last">
                        naručeno od dobavljača — ne planira se dok ne stigne
                        {' · '}
                        {/* Roba ume da stigne pre nego sto udje kalkulacija.
                            Tada je poslovodja vidi u magacinu, a ERP jos ne. */}
                        <button
                          type="button"
                          className="link"
                          disabled={stigla === delivery.documentKey}
                          onClick={() => void javiDaJeStigla(delivery.documentKey)}
                        >
                          {stigla === delivery.documentKey
                            ? 'Upisujem…'
                            : 'roba je stigla, kalkulacija još nije'}
                        </button>

                        {padPoDokumentu[delivery.documentKey] && (
                          <span className="sourcing__pad">
                            {' '}— nije upisano: {padPoDokumentu[delivery.documentKey]}
                          </span>
                        )}
                      </span>
                    )}

                    {item.proposedSource && !item.decidedSource && (
                      <span className="sourcing__last">
                        trgovac upisao: {item.proposedName ?? item.proposedSource}
                      </span>
                    )}

                    {/* Mesto koje drži tačno koliko treba je najčešće izložbeni
                        komad. Ne tvrdi se, samo se istakne da se vidi. */}
                    {item.sources.some((o) => o.available === item.quantity)
                      && item.sources.length > 1 && (
                      <span className="sourcing__last">poslednji komad na nekom mestu</span>
                    )}

                    {item.decidedBy && (
                      <span className="muted sourcing__who">odlučio {item.decidedBy}</span>
                    )}
                    </span>
                  </li>
                ))}
              </ul>

              {fale.length > 0 && (
                <p className="sourcing__missing">
                  Još nemaju mesto: <strong>{fale.map((i) => i.articleName).join(', ')}</strong>.
                  Dok se i za njih ne izabere odakle se diže, dugme stoji.
                </p>
              )}

              <button
                type="button"
                className="primary"
                disabled={busy === delivery.documentKey || !decided || potvrdjeno}
                onClick={() => setPotvrdaZa(delivery)}
              >
                {busy === delivery.documentKey
                  ? 'Čuvam…'
                  : potvrdjeno ? 'Potvrđeno' : palo.length > 0 ? 'Potvrdi ponovo' : 'Potvrdi odakle ide'}
              </button>

              {palo.map((i) => (
                <p key={i.articleCode} className="sourcing__stale">
                  {i.articleName}: prenos u 012 nije prošao — roba nije odvojena i drugi
                  objekat je može prodati. {i.reservationFailed} Potvrdi ponovo; ako opet
                  ne prođe, javi operateru.
                </p>
              ))}

              {/* Klik mora nesto da kaze. Dok ovoga nije bilo, covek je
                  pritiskao ponovo jer se na ekranu nista nije promenilo. */}
              {upravoSacuvano && (
                <p className="sourcing__reserved">
                  Sačuvano u {upravoSacuvano.kad} — {upravoSacuvano.stavki}{' '}
                  {upravoSacuvano.stavki === 1 ? 'stavka' : 'stavki'}.
                  {' '}Magacin ovo vidi odmah.
                </p>
              )}

              {/* Kupac je robu uzeo ovde, umesto da čeka vozilo. Ne bira se
                  mesto — isporuka izlazi iz prevoza. Za robu koja tek stiže u
                  objekat to ne važi: preuzimanje se beleži kad stigne. */}
              {delivery.uObjekat ? (
                <p className="muted sourcing__who">
                  Roba stiže u objekat turom logistike. Kad stigne, „Kupac preuzeo" je u
                  odeljku „Roba za kupce".
                </p>
              ) : (
                <button
                  type="button"
                  className="link"
                  disabled={busy === delivery.documentKey}
                  onClick={() => setPreuzima(delivery)}
                >
                  Kupac preuzeo ovde
                </button>
              )}
              {preuzetoIshod[delivery.documentKey] && (
                <p className="sourcing__reserved">{preuzetoIshod[delivery.documentKey]}</p>
              )}

              {potvrdjeno && !upravoSacuvano && delivery.items.some((i) => i.decidedBy) && (
                <p className="muted sourcing__who">
                  Već potvrđeno. Promeni neko mesto ako treba drugačije — dok isporuka
                  nije na turi.
                </p>
              )}

                  {(reservations[delivery.documentKey] ?? []).map((outcome) => (
                    <p
                      key={outcome.articleCode}
                      className={
                        outcome.reserved
                          ? 'sourcing__reserved'
                          : outcome.noTransferNeeded
                            ? 'muted'
                            : 'sourcing__stale'
                      }
                    >
                      {outcome.articleCode}: {outcome.message}
                    </p>
                  ))}
                </div>
              );
            })}
          </li>
        ))}
      </ul>

      {potvrdaZa && (
        <Dialog
          title="Potvrdi odakle ide roba?"
          description={
            <>
              Otpremnica <strong>{potvrdaZa.documentKey}</strong>:
              <br />
              {potvrdaZa.items
                .map((i) => {
                  const izabrano = chosenFor(potvrdaZa, i);
                  const mesto = i.sources.find((o) => o.code === izabrano)?.name
                    ?? (i.proposedSource === izabrano ? (i.proposedName ?? izabrano) : izabrano);
                  return izabrano ? `${i.articleName} — iz ${mesto}` : null;
                })
                .filter((red): red is string => red !== null)
                .join('; ')}
              <br /><br />
              Ovo <strong>odmah pravi nalog u ERP-u</strong> — prenos u magacin 012,
              da taj komad niko drugi ne proda (osim kad je roba već kod tebe). Ako
              menjaš ranije izabrano mesto, stari nalog se briše ako još nije
              proknjižen; ako jeste, aplikacija kaže šta prvo treba uraditi.
            </>
          }
          confirmLabel="Da, potvrdi"
          busy={busy === potvrdaZa.documentKey}
          onCancel={() => setPotvrdaZa(null)}
          onConfirm={() => {
            const d = potvrdaZa;
            setPotvrdaZa(null);
            void save(d);
          }}
        />
      )}

      {preuzima && (
        <Dialog
          title="Kupac je preuzeo robu ovde?"
          description={
            <>
              Otpremnica <strong>{preuzima.documentKey}</strong>:{' '}
              {preuzima.items.map((i) => i.articleName).join(', ')}.
              <br /><br />
              Isporuka <strong>izlazi iz prevoza</strong> — operater je više neće
              videti za razvoz. U ERP se ništa ne upisuje, jer roba nije putovala.
              Potvrdi samo ako je kupac stvarno uzeo robu, pa{' '}
              <strong>otpremnicu u ERP-u kukiči kao isporučenu</strong>.
            </>
          }
          confirmLabel="Da, preuzeo je"
          busy={busy === preuzima.documentKey}
          onCancel={() => setPreuzima(null)}
          onConfirm={() => void preuzeoOvde()}
        />
      )}

      {promising && (
        <Dialog
          title={`Naručeno: ${promising.item.articleName}`}
          description={
            <>
              Dok obećanje stoji, <strong>ova roba se neće dizati ni sa jednog
              drugog mesta</strong> — izložbeni komad iz druge radnje nije
              odgovor na porudžbinu koju dobavljač isporučuje. Operater umesto
              toga vidi gde i kad roba stiže, pa neće tražiti zamenu ni zvati
              kupca bez potrebe.
              <br /><br />
              Isporuka zbog toga <strong>čeka</strong> dok roba ne legne na
              obećano mesto. Kad legne, obećanje samo prestaje da važi i to mesto
              postaje izvor. Ništa se ne rezerviše i ništa ne ulazi u ERP.
            </>
          }
          confirmLabel="Javi operateru"
          busy={busy === promising.document}
          disabled={promiseWhere === '' || promiseWhen === ''}
          onCancel={() => setPromising(null)}
          onConfirm={() => void promise()}
        >
          <label>
            Gde stiže
            <select value={promiseWhere} onChange={(e) => setPromiseWhere(e.target.value)}>
              <option value="">— izaberi —</option>
              {places.map((place) => (
                <option key={`${place.kind}:${place.code}`} value={`${place.kind}:${place.code}`}>
                  {place.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Kad se očekuje
            <input
              type="date"
              value={promiseWhen}
              onChange={(e) => setPromiseWhen(e.target.value)}
            />
          </label>

          <label>
            Napomena (može ostati prazno)
            <input
              value={promiseNote}
              onChange={(e) => setPromiseNote(e.target.value)}
              placeholder="npr. potvrđeno kod dobavljača, kamion u sredu"
            />
          </label>
        </Dialog>
      )}
    </>
  );
}
